import { useReducedMotion } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import IcOutlineMusicNote from '~icons/ic/outline-music-note'
import { useGetMedia } from 'src/services/apollo/media'
import {
  useAudioMediaUrl,
  useReferenceVideoThumbnailId,
} from 'src/services/orpc/media'
import { cn } from 'src/utils/cx'
import {
  getImageThumbnailUrl,
  getPublicUrl,
  getThumbnailUrl,
  getThumbnailVariantUrl,
} from 'src/utils/media'
import { formatDuration } from '../../BottomBar/MultiRefResourceCompactSelector/utils'
import type { MediaKind } from '../hooks'

export interface MediaMentionHoverPreviewProps {
  mediaId: string
  kind: MediaKind
  label: string
  className?: string
  /**
   * Optional caller-provided fallbacks. Useful when the consumer holds
   * data that isn't on the GraphQL `media` payload — e.g. blob URLs for
   * just-uploaded refs in the editor, or zero local state in artwork
   * detail. Both fields are consulted before the media-resolver fallback
   * chain.
   */
  localUrl?: string
  localDuration?: number
}

const CARD_BASE =
  'overflow-hidden rounded-xl border border-neutral-200 bg-background shadow-2xl backdrop-blur-sm dark:border-neutral-700'

/**
 * Rich hover preview card for an `@mention` chip in the multi-ref prompt.
 * Renders kind-specific content: large image, autoplaying muted looped video,
 * or a native audio player. Positioning is handled by the parent.
 */
export function MediaMentionHoverPreview({
  mediaId,
  kind,
  label,
  className,
  localUrl,
  localDuration,
}: MediaMentionHoverPreviewProps) {
  const { t } = useTranslation(['generation'])
  const { data: media } = useGetMedia(mediaId)
  // Read initial value synchronously to avoid a one-frame flash of autoplay
  // motion for users with `prefers-reduced-motion: reduce`.
  const reducedMotion = useReducedMotion(false, {
    getInitialValueInEffect: false,
  })
  /**
   * Videos only trust the resolved `urls[THUMBNAIL]` variant; the synthesized
   * `media.thumbnailUrl` 404s for non-image media and would otherwise block
   * the oRPC fallback below. Images keep the existing behaviour.
   */
  const mediaPoster =
    kind === 'video' ? getThumbnailVariantUrl(media) : getThumbnailUrl(media)
  /**
   * Fallback poster for uploaded videos whose media payload lacks a
   * THUMBNAIL variant. Hook must be called unconditionally; gating is done
   * via the `enabled` flag inside `useReferenceVideoThumbnailId`.
   */
  const { data: videoThumbId } = useReferenceVideoThumbnailId(
    kind === 'video' && !mediaPoster ? mediaId : undefined,
  )
  /**
   * Audio is uploaded via oRPC and not resolvable via the GraphQL `media()`
   * resolver. Prefer the caller-supplied `localUrl` (e.g. a blob URL captured
   * at upload time in the editor); fall back to the oRPC `audio/url`
   * endpoint for prefilled refs without a local item (history pick,
   * shared-link prefill, artwork detail viewer). Called unconditionally to
   * respect rules-of-hooks.
   */
  const localAudioSrc =
    kind === 'audio'
      ? (localUrl ?? media?.fileUrl ?? getPublicUrl(media))
      : undefined
  const { data: fetchedAudioSrc } = useAudioMediaUrl(
    kind === 'audio' && !localAudioSrc ? mediaId : undefined,
  )

  if (kind === 'image') {
    const src = getPublicUrl(media) ?? getImageThumbnailUrl(mediaId, media)
    return (
      <div
        tabIndex={-1}
        className={cn(CARD_BASE, 'w-[260px]', className)}
        onMouseDown={e => e.preventDefault()}
      >
        <div className="relative min-h-[320px] bg-neutral-100 dark:bg-neutral-900">
          {src ? (
            <img
              src={src}
              alt={label}
              loading="eager"
              decoding="async"
              className="block max-h-[320px] w-full object-contain"
            />
          ) : (
            <div className="flex h-40 w-full items-center justify-center text-xs text-neutral-400">
              …
            </div>
          )}
          <div className="pointer-events-none absolute left-2 top-2 rounded-md bg-black/60 px-2 py-0.5 text-xs font-medium text-white backdrop-blur-sm">
            {label}
          </div>
        </div>
      </div>
    )
  }

  if (kind === 'video') {
    const videoSrc = media?.fileUrl ?? getPublicUrl(media)
    const poster =
      mediaPoster ??
      (videoThumbId ? getImageThumbnailUrl(videoThumbId) : undefined)
    const duration = formatDuration(media?.duration ?? localDuration ?? 0)
    return (
      <div
        tabIndex={-1}
        className={cn(CARD_BASE, 'w-[260px]', className)}
        onMouseDown={e => e.preventDefault()}
      >
        <div className="relative bg-neutral-900">
          {videoSrc ? (
            <video
              src={videoSrc}
              poster={poster}
              autoPlay={!reducedMotion}
              muted
              loop
              playsInline
              preload="metadata"
              controls={false}
              className="block max-h-[360px] w-full object-contain"
            />
          ) : poster ? (
            <img
              src={poster}
              alt={label}
              className="block max-h-[360px] w-full object-contain"
            />
          ) : (
            <div className="h-40 w-full" />
          )}
          <div className="pointer-events-none absolute left-2 top-2 rounded-md bg-black/60 px-2 py-0.5 text-xs font-medium text-white backdrop-blur-sm">
            {label}
          </div>
          {duration && (
            <div className="pointer-events-none absolute bottom-2 left-2 rounded bg-black/60 px-1.5 py-0.5 text-[11px] font-medium leading-none text-white">
              {duration}
            </div>
          )}
        </div>
      </div>
    )
  }

  const audioSrc = localAudioSrc ?? fetchedAudioSrc
  return (
    <div
      tabIndex={-1}
      className={cn(CARD_BASE, 'w-[280px] p-3', className)}
      onMouseDown={e => e.preventDefault()}
    >
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-primary">
        <IcOutlineMusicNote className="size-4" />
        <span>{label}</span>
      </div>
      {audioSrc ? (
        <audio src={audioSrc} controls preload="metadata" className="w-full" />
      ) : (
        <div className="rounded-md bg-neutral-100 px-2 py-2 text-xs text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
          {t(
            'generation:image-to-video.multi-ref.mention.preview-unavailable',
            {
              defaultValue: 'Preview unavailable',
            },
          )}
        </div>
      )}
    </div>
  )
}
